import { useIntl } from "react-intl";
import InstagramSVG from "src/design-system/icons/Instagram.svg?react";
import LinkedInSVG from "src/design-system/icons/Linkedin.svg?react";
import FacebookSVG from "src/design-system/icons/Facebook.svg?react";
import PinterestSVG from "src/design-system/icons/Pinterest.svg?react";
import { useTypedLocation } from "src/utils/hooks/useTypedLocation";
import { CarLink } from "src/components/GlobalHeader/CarLink";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { useLanguageCode } from "src/utils/hooks/useLanguageCode";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { ExitLink } from "src/exits/components/ExitLink";
import { useFeature } from "src/feature/useFeature";
import { RefreshingAd } from "src/components/RefreshingAd/RefreshingAd";
import { DisplayAd } from "src/components/DisplayAd/DisplayAd";
import messages from "./Footer.messages";

const supportedLanguages = ["en", "fr", "es"];

function getSupportedHelpCenterLanguages(language: string) {
  return supportedLanguages.includes(language) ? language : "en";
}

export function Footer() {
  const intl = useIntl();
  const currentYear = new Date().getFullYear();
  const { pathname } = useTypedLocation();
  const language = useLanguageCode();
  const { isMobile } = useScreenMinWidth();
  const refreshAdUnits = useFeature("RefreshAdUnits");

  function logInternalExit(label: string) {
    sendAnalyticsInteractionEvent({
      category: "Footer",
      action: `Click:InternalExit`,
      label,
    });
  }

  const DesktopFooterAd = refreshAdUnits ? RefreshingAd : DisplayAd;

  return (
    <footer className="flex flex-col items-center gap-8 bg-surface-hover px-4 pt-12 pb-18 lg:gap-18 lg:px-6 lg:pb-8">
      {!isMobile && (
        <DesktopFooterAd
          variant="DisplayAd"
          slotConfigId="desktopFooterLeaderboard"
          adHeight={90}
        />
      )}
      <div className="flex w-full max-w-[var(--breakpoint-xl)] flex-col gap-8 border-b-1 border-border pb-8 md:flex-row lg:gap-12 lg:pb-12">
        <nav
          className="flex w-full flex-col gap-6 md:w-56"
          aria-labelledby="discover-heading"
        >
          <h3 id="discover-heading" className="text-heading-sm">
            {intl.formatMessage(messages.discoverHeading)}
          </h3>
          <ul className="flex flex-col gap-4">
            <li>
              <a
                className="leading-tight hover:underline"
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("landing-page")}
              >
                {intl.formatMessage(messages.transportationLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href={`${pathname}#h`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("hotels")}
              >
                {intl.formatMessage(messages.hotelsLink)}
              </a>
            </li>
            <li>
              <CarLink
                className="leading-tight hover:underline"
                eventCategory="Footer"
              >
                {intl.formatMessage(messages.rentalCarsLink)}
              </CarLink>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href="/about/rome2rio-app/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("get-app")}
              >
                {intl.formatMessage(messages.getAppLink)}
              </a>
            </li>
          </ul>
        </nav>

        <nav
          className="flex w-full flex-col gap-6 md:w-56"
          aria-labelledby="company-heading"
        >
          <h3 id="company-heading" className="text-heading-sm">
            {intl.formatMessage(messages.companyHeading)}
          </h3>
          <ul className="flex flex-col gap-4">
            <li>
              <a
                className="leading-tight hover:underline"
                href="/about/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("about")}
              >
                {intl.formatMessage(messages.aboutUsLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href="/careers/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("careers")}
              >
                {intl.formatMessage(messages.careersLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href="/advertise/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("advertise")}
              >
                {intl.formatMessage(messages.advertiseLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href="/media/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("media")}
              >
                {intl.formatMessage(messages.mediaLink)}
              </a>
            </li>
          </ul>
        </nav>
        <nav
          className="flex w-full flex-col gap-6 md:w-56"
          aria-labelledby="support-heading"
        >
          <h3 id="support-heading" className="text-heading-sm">
            {intl.formatMessage(messages.supportHeading)}
          </h3>
          <ul className="flex flex-col gap-4">
            <li>
              <a
                className="leading-tight hover:underline"
                href={`https://help.rome2rio.com/${getSupportedHelpCenterLanguages(language)}/support/home`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("help")}
              >
                {intl.formatMessage(messages.helpLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href={`https://help.rome2rio.com/${language}/support/tickets/new`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("contact-us")}
              >
                {intl.formatMessage(messages.contactUsLink)}
              </a>
            </li>
            <li>
              <a
                className="leading-tight hover:underline"
                href="/get-listed/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => logInternalExit("get-listed")}
              >
                {intl.formatMessage(messages.getListedLink)}
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <div className="flex w-full max-w-[var(--breakpoint-xl)] flex-col gap-6 md:flex-row md:gap-12">
        <nav className="flex flex-1 flex-wrap gap-4 text-xs leading-none">
          <div className="basis-full md:basis-auto">
            &copy; {currentYear} Rome2Rio Pty Ltd
          </div>
          <a
            className="tracking-tight hover:underline"
            href="/legal/data-sources"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logInternalExit("data-sources")}
          >
            {intl.formatMessage(messages.dataSourcesLink)}
          </a>
          <a
            className="tracking-tight hover:underline"
            href="/legal/photocredits"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logInternalExit("photocredits")}
          >
            {intl.formatMessage(messages.photoCreditsLink)}
          </a>
          <a
            className="tracking-tight hover:underline"
            href="/company-disclosure"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logInternalExit("company-disclosure")}
          >
            {intl.formatMessage(messages.companyDisclosureLink)}
          </a>
          <a
            className="tracking-tight hover:underline"
            href="/terms-of-use"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logInternalExit("terms-of-use")}
          >
            {intl.formatMessage(messages.termsLink)}
          </a>
          <a
            className="tracking-tight hover:underline"
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => logInternalExit("privacy-policy")}
          >
            {intl.formatMessage(messages.privacyPolicyLink)}
          </a>
        </nav>
        <nav className="flex flex-row gap-4">
          <ExitLink
            className="block h-6 w-6 text-icon-primary hover:text-icon-primary-hover"
            aria-label="Instagram"
            href="https://www.instagram.com/rome2rio_official"
            target="_blank"
            rel="noopener noreferrer"
            kind="Social"
            exitPoint={{
              view: "exploreFooter",
              trigger: "footerSocialCTA",
            }}
            onExit={({ sendExitInteractionEvent }) =>
              sendExitInteractionEvent({
                category: "Footer",
                action: "Click:SocialExit",
                label: "Instagram",
              })
            }
          >
            <InstagramSVG className="h-full w-full" />
          </ExitLink>
          <ExitLink
            className="block h-6 w-6 text-icon-primary hover:text-icon-primary-hover"
            aria-label="LinkedIn"
            href="https://www.linkedin.com/company/rome2rio-pty-ltd"
            target="_blank"
            rel="noopener noreferrer"
            kind="Social"
            exitPoint={{
              view: "exploreFooter",
              trigger: "footerSocialCTA",
            }}
            onExit={({ sendExitInteractionEvent }) =>
              sendExitInteractionEvent({
                category: "Footer",
                action: "Click:SocialExit",
                label: "LinkedIn",
              })
            }
          >
            <LinkedInSVG className="h-full w-full" />
          </ExitLink>
          <ExitLink
            className="block h-6 w-6 text-icon-primary hover:text-icon-primary-hover"
            aria-label="Facebook"
            href="https://www.facebook.com/rome2rio"
            target="_blank"
            rel="noopener noreferrer"
            kind="Social"
            exitPoint={{
              view: "exploreFooter",
              trigger: "footerSocialCTA",
            }}
            onExit={({ sendExitInteractionEvent }) =>
              sendExitInteractionEvent({
                category: "Footer",
                action: "Click:SocialExit",
                label: "Facebook",
              })
            }
          >
            <FacebookSVG className="h-full w-full" />
          </ExitLink>
          <ExitLink
            className="block h-6 w-6 text-icon-primary hover:text-icon-primary-hover"
            aria-label="Pinterest"
            href="https://au.pinterest.com/rome2rio/"
            target="_blank"
            rel="noopener noreferrer"
            kind="Social"
            exitPoint={{
              view: "exploreFooter",
              trigger: "footerSocialCTA",
            }}
            onExit={({ sendExitInteractionEvent }) =>
              sendExitInteractionEvent({
                category: "Footer",
                action: "Click:SocialExit",
                label: "Pinterest",
              })
            }
          >
            <PinterestSVG className="h-full w-full" />
          </ExitLink>
        </nav>
      </div>
    </footer>
  );
}
